/**
 * useChatToggle — the one rule for whether the chat dock can be opened, and the
 * action that opens or closes it.
 *
 * Two controls read it: the desktop edge handle and the phone's top-row button.
 * Sharing the hook is what keeps their visibility rules from drifting apart.
 */
import { useLocation } from 'react-router-dom';

import { useChatDockController } from './useChatDockController';
import { selectActiveDescriptor, useChatDockRegistry } from './useChatDockRegistry';

interface ChatToggle {
  /** False on /chat (the dock is excluded there) and when no surface is registered. */
  visible: boolean;
  isOpen: boolean;
  toggle: () => void;
}

/** Whether a chat opener should render, whether the dock is open, and the toggle. */
export function useChatToggle(): ChatToggle {
  const { pathname } = useLocation();
  const dock = useChatDockController();
  const descriptors = useChatDockRegistry(s => s.descriptors);
  const activeOwnerId = useChatDockRegistry(s => s.activeOwnerId);

  const visible =
    !pathname.startsWith('/chat') && selectActiveDescriptor({ descriptors, activeOwnerId }) !== null;

  return { visible, isOpen: dock.isOpen, toggle: dock.toggle };
}
